import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FaCheck, FaFileImport} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import axios from "axios";
import {FaTriangleExclamation} from "react-icons/fa6";
import projectUtils from "../../../../../utils/project-utils.js";
import AlertMessages from "../../../../../components/messages/alert-message.js";
import {Modal} from "../../../../../components/modal/modal.jsx"
import Button from "../../../../../components/button/button.jsx";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import ImportCellRenderer from "./import-cell-renderer.jsx";
import userService from "../../../../../services/web/user-service.js";
import Options from "../../../../../utils/options-utils.js";
import AgGridUtils from "../../../../../utils/aggrid-utils.js";

const acceptedFileTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const ImportData = ({openModal, modal, refetch, setIsLoading}) => {
    const [users, setUsers] = useState([])
    const [file, setFile] = useState(null)

    useEffect(() => {
        if (!modal) {
            setUsers([])
            setFile(null)
        }
    }, [modal]);

    const gridRef = useRef();
    const [columnDefs, setColumnDefs] = useState([
        {headerName: "", field: "", width: 50,
            cellRenderer: ({data}) => {
                if (data.errors.length > 0)
                    return <span className={"w-full h-full flex items-center justify-center"}><FaTriangleExclamation color="red" /></span>

                if (data.errors.length === 0)
                    return <span className={"w-full h-full flex items-center justify-center"}><FaCheck color="green" /></span>

                return ""
            }
        },
        {headerName: "Nome", field: "user.name"},
        {headerName: "CPF", field: "user.cpf", width: 100},
        {headerName: "E-mail", field: "user.email"},
        {headerName: "Sexo", field: "user.profile.sex"},
        {headerName: "Nascimento", field: "user.profile.dtBirth"},
        {headerName: "Naturalidade", field: "user.profile.nationality"},
        {headerName: "Escolaridade", field: "user.profile.educationLevel",
            cellRenderer: ({value}) => {
                const find = Options.educationLevel.find(e => e.value === value);
                if (find) return find.label;
                return null
            }
        },
        {headerName: "Estado civil", field: "user.profile.maritalStatus"},
        {headerName: "Mãe", field: "user.profile.mother"},
        {headerName: "Pai", field: "user.profile.father"},
        {headerName: "Fator sanguíneo", field: "user.profile.bloodFactor"},
        {headerName: "Canhoto?", field: "user.profile.leftHanded", cellRenderer: ({value}) => (value ? 'Sim' : 'Não')},
        {headerName: "Tipo", field: "user.address.type"},
        {headerName: "Logradouro", field: "user.address.address"},
        {headerName: "Número", field: "user.address.number"},
        {headerName: "Complemento", field: "user.address.complement"},
        {headerName: "Bairro", field: "user.address.neighborhood"},
        {headerName: "Município", field: "user.address.city"},
        {headerName: "CEP", field: "user.address.zipcode"},
        {headerName: "Nome de guerra", field: "user.nameWar"},
        {headerName: "Número funcional", field: "user.employeeCode"},
        {headerName: "Patente", field: "user.rank"},
        {headerName: "Equipe", field: "teamName" },
        {headerName: "Setor", field: "user.profile.sector"},
        {headerName: "Admissão", field: "user.profile.admission"},
        {headerName: "Login", field: "user.login"},
        {headerName: "Contingente?", field: "contingent", cellRenderer: ({value}) => (value ? 'Sim' : 'Não')},
        {headerName: "Efetivo?", field: "user.effectiveStatus", cellRenderer: ({value}) => (value ? 'Sim' : 'Não')},
        {headerName: "Número celular", field: "cellPhone"},
        {headerName: "Número residencial", field: "residentialPhone"},
        {headerName: "RG número", field: "user.sentry-doc.rg"},
        {headerName: "RG emissor", field: "user.sentry-doc.emitterRg"},
        {headerName: "RG data de expedição", field: "user.documents.expeditionRg"},
        {headerName: "CNH número", field: "user.sentry-doc.cnh"},
        {headerName: "CNH data de emissão", field: "user.documents.emitterCnh"},
        {headerName: "CNH data de vencimento", field: "user.documents.expirationCnh"},
        {headerName: "CNH categoria", field: "user.documents.categoryCnh"},
        {headerName: "CTPS número", field: "user.sentry-doc.workRegister"},
        {headerName: "CTPS série", field: "user.sentry-doc.workSerie"},
        {headerName: "CTPS data de emissão", field: "user.documents.emitterWork"},
        {headerName: "PIS/PASEP número", field: "user.sentry-doc.pisPasep"},
        {headerName: "Título de eleitor número", field: "user.sentry-doc.voterRegister"},
        {headerName: "Título de eleitor zona", field: "user.sentry-doc.voterZone"},
        {headerName: "Título de eleitor seção", field: "user.sentry-doc.voterSection"},
        {headerName: "Certificado de reservista número RA", field: "user.sentry-doc.reservistRaNumber"},
        {headerName: "Certificado de reservista vencimento", field: "user.sentry-doc.reservistDate"},
    ]);

    const defaultColDef = useMemo(() => ({
        cellRenderer: ImportCellRenderer
    }), []);

    const loadFile = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = acceptedFileTypes.join(',');
        input.onchange = event => createImportPreview(event.target.files[0])
        input.click();
    }

    const createImportPreview = (file) => {
        if (!file || !acceptedFileTypes.includes(file.type)) return;
        setIsLoading(true);

        const formData = new FormData();
        formData.append('file', file);

        axios.post(`${API_KEEPER}/user/import/preview`, formData)
            .then(({data}) => {
                setUsers(data)
                setFile(file)
            })
            .catch(error => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível carregar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            }).finally(() => setIsLoading(false));
    }

    useEffect(() => {
        if (gridRef.current && users.length > 0) setTimeout(() => gridRef.current.api.autoSizeAllColumns(), 0);
    }, [users]);

    const confirm = () => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        userService.userImport(formData)
            .then(({data}) => {
                const {success, errors} = data;
                projectUtils.swalMessage('warning', 'Aviso', `Agentes salvos: ${success} <br /> Agentes com erro: ${errors}`, 'Ok');
            })
            .catch(error => {
                console.error(error);
                const {message} = error.response.data;
                let errMessage = AlertMessages[message] ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            }).finally(() => {
                openModal(false);
                setFile(null)
                setUsers([]);
                refetch();
                setIsLoading(false);
            }
        );
    }

    const clear = () => {
        setUsers([])
        setFile(null)
    }

    const downloadTemplate = () => {
        window.open(`${API_KEEPER}/user/import/template`);
    }

    const dragOver = (e) => e.preventDefault();
    const dropFile = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            createImportPreview(files[0]);
        }
    }

    return (
        <Modal open={modal} setOpen={() => openModal(false)} centered size="xl" backdrop="static" keyboard={false} title={"Importar agentes"}>
            <Modal.Body className={"p-2"}>
                {
                    file !== null &&
                    <>
                        <div style={{height: "calc(100vh - 240px)"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={users}
                                columnDefs={columnDefs}
                                animateRows={false}
                                suppressCellFocus={true}
                                defaultColDef={defaultColDef}
                                suppressRowVirtualisation={true}
                                suppressColumnVirtualisation={true}
                                domLayout={"normal"}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                        <div className="flex gap-2 p-2 bg-[#efefef]">
                            <div className={"flex items-center justify-center"}>Total de agentes:
                                <span className={"ml-1 border border-[#555] w-[40px] h-[20px] font-bold text-[12px] flex items-center justify-center rounded-[3px]"}>{users.length}</span>
                            </div>
                            <div className={"flex items-center justify-center"}>
                                OK:
                                <span className={"ml-1 bg-[#008a0e] w-[40px] h-[20px] font-bold text-[12px] flex items-center justify-center rounded-[3px] text-white"}>
                                    {users.filter(u => u.errors.length === 0).length}
                                </span>
                            </div>
                            <div className={"flex items-center justify-center"}>Com problema:
                                <span className={"ml-1 bg-[#cb0000] w-[40px] h-[20px] text-white text-flex font-bold text-[12px] flex items-center justify-center rounded-[3px]"}>
                                    {users.filter(u => u.errors.length > 0).length}
                                </span>
                            </div>
                        </div>
                    </>
                }

                {
                    !file &&
                    <div onDrop={dropFile} onDragOver={dragOver} onClick={loadFile} style={{height: "calc(100vh - 240px)"}}>
                        <div className="h-full flex justify-center items-center border border-black border-dashed cursor-pointer select-none hover:opacity-70">
                            <h2 className={"text-[24px]"}>Clique aqui ou arraste o arquivo para fazer a importação.</h2>
                        </div>
                    </div>

                }
            </Modal.Body>

            <Modal.Footer>
                <div className={"w-full flex justify-between"}>
                    <Button target={"success"} onClick={downloadTemplate}>
                        <FaFileImport /> Baixar modelo
                    </Button>
                    <div className={"flex justify-between gap-2"}>
                        <Button target={"secondary"} onClick={clear}>
                            Limpar seleção
                        </Button>
                        <Button target={"secondary"} disabled={users.length === 0 || users.filter(u => u.errors.length > 0).length > 0} onClick={confirm}>
                            Confirmar importação
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportData;